import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, CheckCircle, MapPin, Star, Clock, AlertTriangle, Bell, RotateCcw, X, UserPlus, Heart, Users, Truck } from 'lucide-react';
import { supabase } from '../supabase';
import { useToast } from '../context/ToastContext';
import { useRefresh } from '../context/RefreshContext';
import { calcularEAtribuirBadges, BADGE_DEFS } from '../utils/badges';

function tipoIcone(tipo, urgente) {
  const color = urgente ? '#DC2626' : '#5B3CE4';
  const p = { size: 18, color, strokeWidth: 2 };
  const green = { ...p, color: '#1FA971' };
  const red = { ...p, color: '#E85757' };
  const mapa = {
    nova_solicitacao: <BookOpen {...p} />,
    emprestimo_aprovado: <CheckCircle {...p} />,
    entrega_proposta: <MapPin {...p} />,
    entrega_agendada: <MapPin {...p} />,
    entrega_combinada: <CheckCircle {...green} />,
    entrega_aguardando: <CheckCircle {...p} />,
    devolucao_aguardando: <RotateCcw {...p} />,
    leitor_diz_devolveu: <RotateCcw {...p} />,
    avaliar_livro: <Star {...p} />,
    avaliar_leitor: <Star {...p} />,
    vencimento_hoje: <Clock {...p} />,
    vencimento_atrasado: <AlertTriangle {...p} />,
    lembrete_dono: <Clock {...p} />,
    leitor_avisou_atraso: <AlertTriangle {...p} />,
    ja_tenho_livro: <CheckCircle {...green} />,
    livro_entregue: <CheckCircle {...green} />,
    devolucao_iniciar_pessoalmente: <MapPin {...p} />,
    proposta_devolucao: <MapPin {...p} />,
    devolucao_agendada: <RotateCcw {...p} />,
    emprestimo_inadimplente: <AlertTriangle {...red} />,
    solicitacao_amizade: <UserPlus {...p} />,
    amizade_aceita: <Heart {...p} />,
    novo_seguidor: <UserPlus {...p} />,
    livro_curtido: <Heart {...p} />,
    novo_interesse: <Heart {...p} />,
    novo_livro_clube: <BookOpen {...p} />,
    proposta_dono: <BookOpen {...p} />,
    novo_membro_clube: <Users {...p} />,
    clube_aprovado: <CheckCircle {...p} />,
    clube_rejeitado: <X {...p} />,
    novo_clube_amigo: <Users {...p} />,
    mensagem_clube: <Bell {...p} />,
    emprestimo_recusado: <X {...p} />,
    emprestimo_cancelado: <X {...p} />,
    emprestimo_cancelado_prazo: <AlertTriangle {...p} />,
    solicitacao_expirada: <AlertTriangle {...p} />,
    livro_disponivel: <Bell {...p} />,
    devolucao_enviada: <Truck {...p} />,
    emprestimo_registrado_dono: <BookOpen {...green} />,
    solicitacao_devolucao: <RotateCcw {...p} />,
  };
  return mapa[tipo] || <Bell {...p} />;
}

function SecaoEstrelas({ label, estrelas, onEstrelas, comentario, onComentario, placeholder }) {
  return (
    <div className="bg-paper rounded-xl p-4">
      <p className="text-xs font-medium text-ink-700 mb-2">{label} <span className="font-normal text-ink-400">(opcional)</span></p>
      <div className="flex gap-2 mb-3">
        {[1,2,3,4,5].map(s => (
          <button key={s} onClick={() => onEstrelas(s)}
            className={`text-2xl ${s <= estrelas ? 'text-amber-400' : 'text-ink-300'}`}>★</button>
        ))}
      </div>
      <textarea placeholder={placeholder} value={comentario}
        onChange={e => onComentario(e.target.value)} rows={2}
        className="w-full border border-line-soft rounded-xl px-3 py-2 text-sm bg-white outline-none focus:border-line resize-none" />
    </div>
  );
}

function tempoRelativo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  return new Date(dateStr).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
}

function Notificacoes({ onVoltar, onIrParaClubes }) {
  const { showToast } = useToast();
  const { refreshKey, triggerRefresh } = useRefresh();
  const navigate = useNavigate();

  const [notificacoes, setNotificacoes] = useState([]);
  const [solicitacoesClube, setSolicitacoesClube] = useState([]);
  const [perfisSolicitantes, setPerfisSolicitantes] = useState({});
  const [minhasSolicitacoes, setMinhasSolicitacoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [perfilDonos, setPerfilDonos] = useState({});
  const [perfilSeguidores, setPerfilSeguidores] = useState({});
  const [jaSigoBack, setJaSigoBack] = useState(new Set());
  const [seguindoBack, setSeguindoBack] = useState(new Set());
  const [repLeitoresMap, setRepLeitoresMap] = useState({});

  const [avaliacaoAberta, setAvaliacaoAberta] = useState(null);
  const [estrelasLivro, setEstrelasLivro] = useState(0);
  const [comentarioLivro, setComentarioLivro] = useState('');
  const [estrelasDono, setEstrelasDono] = useState(0);
  const [comentarioDono, setComentarioDono] = useState('');
  const [salvando, setSalvando] = useState(false);

  const [avaliacaoLeitorAberta, setAvaliacaoLeitorAberta] = useState(null);
  const [estrelasLeitorAv, setEstrelasLeitorAv] = useState(0);
  const [comentarioLeitorAv, setComentarioLeitorAv] = useState('');

  const [processando, setProcessando] = useState(new Set());

  const [cancelamentoAberto, setCancelamentoAberto] = useState(null);
  const [cancelJustificativa, setCancelJustificativa] = useState('');
  const [salvandoCancelamento, setSalvandoCancelamento] = useState(false);

  useEffect(() => {
    buscarNotificacoes();
    verificarVencimentos();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function buscarNotificacoes() {
    setCarregando(true);
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('notificacoes')
      .select('*, emprestimos(*, livros(titulo, autor, user_id, capa_url))')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (!error) {
      let notifData = data || [];

      // Auto-marca avaliar_livro como lida se leitor já avaliou (dado salvo mas notif ficou pendente)
      const jaAvaliados = notifData.filter(n =>
        n.tipo === 'avaliar_livro' && !n.lida &&
        (n.emprestimos?.leitor_feedback_estrelas || n.emprestimos?.leitor_feedback_comentario ||
         n.emprestimos?.avaliacao_dono_estrelas || n.emprestimos?.avaliacao_dono_comentario)
      );
      for (const n of jaAvaliados) {
        await supabase.from('notificacoes').update({ lida: true }).eq('id', n.id);
      }
      if (jaAvaliados.length) {
        const avIds = new Set(jaAvaliados.map(n => n.id));
        notifData = notifData.map(n => avIds.has(n.id) ? { ...n, lida: true } : n);
      }

      // Auto-marca avaliar_leitor como lida se dono já avaliou
      const jaAvaliadosLeitor = notifData.filter(n =>
        n.tipo === 'avaliar_leitor' && !n.lida &&
        (n.emprestimos?.avaliacao_leitor_estrelas || n.emprestimos?.avaliacao_leitor_comentario)
      );
      for (const n of jaAvaliadosLeitor) {
        await supabase.from('notificacoes').update({ lida: true }).eq('id', n.id);
      }
      if (jaAvaliadosLeitor.length) {
        const alIds = new Set(jaAvaliadosLeitor.map(n => n.id));
        notifData = notifData.map(n => alIds.has(n.id) ? { ...n, lida: true } : n);
      }

      setNotificacoes(notifData);

      // Reputação dos leitores para solicitações pendentes de aprovação
      const leitorIdsRep = [...new Set(
        notifData
          .filter(n => n.tipo === 'nova_solicitacao' && !n.lida && n.emprestimos?.leitor_user_id)
          .map(n => n.emprestimos.leitor_user_id)
      )];
      if (leitorIdsRep.length > 0) {
        const [{ data: leitorEmps }, { data: leitorBadges }, { data: leitorPerfis }] = await Promise.all([
          supabase.from('emprestimos')
            .select('leitor_user_id, avaliacao_leitor_estrelas')
            .in('leitor_user_id', leitorIdsRep)
            .gt('avaliacao_leitor_estrelas', 0),
          supabase.from('badges')
            .select('user_id, tipo')
            .in('user_id', leitorIdsRep),
          supabase.from('perfis')
            .select('id, emprestimo_bloqueado')
            .in('id', leitorIdsRep),
        ]);
        const repMap = {};
        leitorIdsRep.forEach(id => {
          const avs = (leitorEmps || []).filter(e => e.leitor_user_id === id).map(e => e.avaliacao_leitor_estrelas);
          const bs = (leitorBadges || []).filter(b => b.user_id === id).map(b => b.tipo);
          const pf = (leitorPerfis || []).find(p => p.id === id);
          repMap[id] = {
            media: avs.length ? (avs.reduce((a, b) => a + b, 0) / avs.length).toFixed(1) : null,
            total: avs.length,
            badges: bs,
            bloqueado: pf?.emprestimo_bloqueado || false,
          };
        });
        setRepLeitoresMap(repMap);
      }

      const donoIds = [...new Set(
        (data || [])
          .filter(n => n.tipo === 'livro_disponivel' && n.emprestimos?.livros?.user_id)
          .map(n => n.emprestimos.livros.user_id)
      )];
      if (donoIds.length > 0) {
        const { data: perfis } = await supabase.from('perfis').select('id, slug, nome').in('id', donoIds);
        const map = {};
        (perfis || []).forEach(p => { map[p.id] = p; });
        setPerfilDonos(map);
      }

      const seguidorIds = [...new Set(
        (data || [])
          .filter(n => n.tipo === 'novo_seguidor' && n.meta?.seguidor_id)
          .map(n => n.meta.seguidor_id)
      )];
      if (seguidorIds.length > 0) {
        const { data: perfisSegs } = await supabase.from('perfis').select('id, nome, slug, username').in('id', seguidorIds);
        const mapSegs = {};
        (perfisSegs || []).forEach(p => { mapSegs[p.id] = p; });
        setPerfilSeguidores(mapSegs);

        const { data: jasSigo } = await supabase.from('seguidores')
          .select('seguido_id').eq('seguidor_id', user.id).in('seguido_id', seguidorIds);
        setJaSigoBack(new Set((jasSigo || []).map(s => s.seguido_id)));
      }
    }

    const { data: adminEntradas } = await supabase
      .from('clube_membros')
      .select('clube_id')
      .eq('user_id', user.id)
      .eq('role', 'admin')
      .eq('status', 'aprovado');

    const adminClubeIds = (adminEntradas || []).map(e => e.clube_id);

    if (adminClubeIds.length > 0) {
      const { data: pendentes } = await supabase
        .from('clube_membros')
        .select('id, clube_id, user_id, status, clubes(id, nome)')
        .in('clube_id', adminClubeIds)
        .eq('status', 'pendente');

      setSolicitacoesClube(pendentes || []);

      const solUserIds = [...new Set((pendentes || []).map(p => p.user_id).filter(Boolean))];
      if (solUserIds.length > 0) {
        const { data: solPerfis } = await supabase.from('perfis').select('id, nome, username').in('id', solUserIds);
        const map = {};
        (solPerfis || []).forEach(p => { map[p.id] = p; });
        setPerfisSolicitantes(map);
      } else {
        setPerfisSolicitantes({});
      }
    } else {
      setSolicitacoesClube([]);
      setPerfisSolicitantes({});
    }

    // Solicitações pendentes que o próprio usuário enviou para entrar em clubes
    const { data: minhasPendentes } = await supabase
      .from('clube_membros')
      .select('id, clube_id, status, clubes(nome)')
      .eq('user_id', user.id)
      .eq('status', 'pendente')
      .eq('role', 'membro');
    setMinhasSolicitacoes(minhasPendentes || []);

    setCarregando(false);
  }

  async function verificarVencimentos() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const hoje = new Date().toISOString().split('T')[0];
    const { data: empAtivos } = await supabase
      .from('emprestimos').select('id, data_devolucao')
      .eq('leitor_user_id', user.id).in('status', ['aprovado', 'em_andamento']);
    if (!empAtivos?.length) return;
    const empIds = empAtivos.map(e => e.id);
    const { data: existentes } = await supabase
      .from('notificacoes').select('emprestimo_id, tipo')
      .eq('user_id', user.id)
      .in('tipo', ['vencimento_hoje', 'vencimento_atrasado'])
      .in('emprestimo_id', empIds);
    const chaves = new Set((existentes || []).map(n => `${n.emprestimo_id}:${n.tipo}`));
    const novas = [];
    for (const emp of empAtivos) {
      if (!emp.data_devolucao) continue;
      if (emp.data_devolucao === hoje && !chaves.has(`${emp.id}:vencimento_hoje`))
        novas.push({ user_id: user.id, emprestimo_id: emp.id, tipo: 'vencimento_hoje', lida: false });
      else if (emp.data_devolucao < hoje && !chaves.has(`${emp.id}:vencimento_atrasado`))
        novas.push({ user_id: user.id, emprestimo_id: emp.id, tipo: 'vencimento_atrasado', lida: false });
    }
    if (novas.length) {
      await supabase.from('notificacoes').insert(novas);
      buscarNotificacoes();
    }
  }

  function setProc(key, val) {
    setProcessando(prev => {
      const next = new Set(prev);
      val ? next.add(key) : next.delete(key);
      return next;
    });
  }

  async function handleAprovar(notif) {
    const emp = notif.emprestimos;
    if (!emp) return;

    const { data: { user } } = await supabase.auth.getUser();
    const { data: meuPerfil } = await supabase.from('perfis').select('whatsapp').eq('id', user.id).maybeSingle();
    if (!meuPerfil?.whatsapp) {
      showToast('Adicione seu WhatsApp no perfil antes de aprovar empréstimos.', 'error');
      return;
    }

    const key = `${notif.id}_aprovar`;
    setProc(key, true);
    try {
      await supabase.from('emprestimos').update({ status: 'aprovado' }).eq('id', emp.id);
      await supabase.from('livros').update({ disponivel: false }).eq('id', emp.livro_id);

      if (emp.leitor_user_id) {
        const { data: leitorPerfil } = await supabase.from('perfis').select('whatsapp').eq('id', emp.leitor_user_id).maybeSingle();
        await supabase.from('notificacoes').insert({
          user_id: emp.leitor_user_id,
          emprestimo_id: emp.id,
          tipo: 'emprestimo_aprovado',
          lida: false,
          meta: {
            dono_whatsapp: meuPerfil.whatsapp,
            leitor_whatsapp: leitorPerfil?.whatsapp || null,
            livro_titulo: emp.livros?.titulo,
          },
        });
      }

      await supabase.from('notificacoes').update({ lida: true }).eq('id', notif.id);
      showToast('Pedido aprovado!', 'success');
      buscarNotificacoes();
      triggerRefresh();
    } catch (err) {
      console.error('handleAprovar:', err);
      showToast('Erro ao aprovar. Tente novamente.', 'error');
    } finally {
      setProc(key, false);
    }
  }

  async function handleRecusar(notif) {
    const emp = notif.emprestimos;
    if (!emp) return;
    const key = `${notif.id}_recusar`;
    setProc(key, true);
    try {
      await supabase.from('emprestimos').update({ status: 'recusado' }).eq('id', emp.id);

      if (emp.leitor_user_id) {
        await supabase.from('notificacoes').insert({
          user_id: emp.leitor_user_id,
          emprestimo_id: emp.id,
          tipo: 'emprestimo_recusado',
          lida: false,
        });
      }

      await supabase.from('notificacoes').update({ lida: true }).eq('id', notif.id);
      showToast('Pedido recusado.', 'info');
      buscarNotificacoes();
    } catch (err) {
      console.error('handleRecusar:', err);
      showToast('Erro ao recusar. Tente novamente.', 'error');
    } finally {
      setProc(key, false);
    }
  }

  async function handleSeguirBack(seguidorId, notifId) {
    setSeguindoBack(prev => new Set([...prev, seguidorId]));
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('seguidores').upsert({ seguidor_id: user.id, seguido_id: seguidorId }, { onConflict: 'seguidor_id,seguido_id' });
    setJaSigoBack(prev => new Set([...prev, seguidorId]));
    setSeguindoBack(prev => { const s = new Set(prev); s.delete(seguidorId); return s; });
    if (notifId) await supabase.from('notificacoes').update({ lida: true }).eq('id', notifId);
    showToast('Seguindo!', 'success');
    buscarNotificacoes();
  }

  async function handleAprovarMembro(membro) {
    const key = `${membro.id}_aprovar`;
    setProc(key, true);

    await supabase.from('clube_membros').update({ status: 'aprovado' }).eq('id', membro.id);
    await supabase.from('notificacoes').insert({
      user_id: membro.user_id,
      tipo: 'clube_aprovado',
      lida: false,
    });

    // Notificar todos os outros membros aprovados do clube
    const { data: outrosMembros } = await supabase
      .from('clube_membros').select('user_id')
      .eq('clube_id', membro.clube_id).eq('status', 'aprovado')
      .neq('user_id', membro.user_id);
    if (outrosMembros?.length > 0) {
      const nomeNovo = perfisSolicitantes[membro.user_id]?.nome || 'Novo membro';
      await supabase.from('notificacoes').insert(
        outrosMembros.map(m => ({
          user_id: m.user_id, tipo: 'novo_membro_clube', lida: false,
          meta: { nome_clube: membro.clubes?.nome || 'clube', clube_id: membro.clube_id, nome_membro: nomeNovo, membro_id: membro.user_id },
        }))
      );
    }

    setProc(key, false);

    const novaLista = solicitacoesClube.filter(s => s.id !== membro.id);
    if (novaLista.length === 0) {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from('notificacoes').update({ lida: true })
        .eq('user_id', user.id).eq('tipo', 'solicitacao_clube');
    }

    buscarNotificacoes();
  }

  async function handleRejeitarMembro(membro) {
    const key = `${membro.id}_rejeitar`;
    setProc(key, true);

    await supabase.from('clube_membros').update({ status: 'rejeitado' }).eq('id', membro.id);
    await supabase.from('notificacoes').insert({
      user_id: membro.user_id,
      tipo: 'clube_rejeitado',
      lida: false,
    });

    setProc(key, false);

    const novaLista = solicitacoesClube.filter(s => s.id !== membro.id);
    if (novaLista.length === 0) {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from('notificacoes').update({ lida: true })
        .eq('user_id', user.id).eq('tipo', 'solicitacao_clube');
    }

    buscarNotificacoes();
  }

  async function handleConfirmarDevolucao(notif) {
    const emp = notif.emprestimos;
    if (!emp) return;
    const key = `${notif.id}_confirmar`;
    setProc(key, true);

    await supabase.from('emprestimos').update({ status: 'devolvido', devolucao_confirmada_dono: true }).eq('id', emp.id);
    await supabase.from('livros').update({ disponivel: true }).eq('id', emp.livro_id);

    if (emp.leitor_user_id) {
      await supabase.from('notificacoes').insert({
        user_id: emp.leitor_user_id,
        emprestimo_id: emp.id,
        tipo: 'avaliar_livro',
        lida: false,
      });
    }

    // Notifica o dono para avaliar o leitor
    if (emp.livros?.user_id) {
      await supabase.from('notificacoes').insert({
        user_id: emp.livros.user_id,
        emprestimo_id: emp.id,
        tipo: 'avaliar_leitor',
        lida: false,
      });
    }

    const { data: avisos } = await supabase
      .from('avisos_disponibilidade')
      .select('id, user_id')
      .eq('livro_id', emp.livro_id)
      .eq('ativo', true);
    if (avisos?.length > 0) {
      await supabase.from('notificacoes').insert(
        avisos.map(a => ({ user_id: a.user_id, emprestimo_id: emp.id, tipo: 'livro_disponivel', lida: false }))
      );
      await supabase.from('avisos_disponibilidade').update({ ativo: false }).in('id', avisos.map(a => a.id));
    }

    if (emp.leitor_user_id) calcularEAtribuirBadges(emp.leitor_user_id);

    await supabase.from('notificacoes').update({ lida: true }).eq('id', notif.id);
    showToast('Devolução confirmada!', 'success');
    setProc(key, false);
    buscarNotificacoes();
  }

  async function handleResolverInadimplente(notif) {
    const emp = notif.emprestimos;
    if (!emp) return;
    const key = `${notif.id}_resolver`;
    setProc(key, true);
    const leitorId = notif.meta?.leitor_user_id || emp.leitor_user_id;
    await supabase.from('emprestimos').update({ status: 'devolvido' }).eq('id', emp.id);
    await supabase.from('livros').update({ disponivel: true }).eq('id', emp.livro_id);
    if (leitorId) {
      await supabase.from('perfis').update({ emprestimo_bloqueado: false }).eq('id', leitorId);
    }
    await supabase.from('notificacoes').update({ lida: true })
      .eq('emprestimo_id', emp.id).eq('tipo', 'emprestimo_inadimplente');
    if (leitorId) {
      await supabase.from('notificacoes').insert({ user_id: leitorId, emprestimo_id: emp.id, tipo: 'avaliar_livro', lida: false });
    }
    if (emp.livros?.user_id) {
      await supabase.from('notificacoes').insert({ user_id: emp.livros.user_id, emprestimo_id: emp.id, tipo: 'avaliar_leitor', lida: false });
    }
    showToast('Devolucao registrada e leitor desbloqueado.', 'success');
    setProc(key, false);
    buscarNotificacoes();
  }

  async function handleCancelarEmprestimo() {
    if (!cancelamentoAberto) return;
    setSalvandoCancelamento(true);
    const notif = cancelamentoAberto;
    const emp = notif.emprestimos;
    if (emp) {
      const { data: { user } } = await supabase.auth.getUser();
      const donoId = emp.livros?.user_id;
      const souDono = user.id === donoId;
      const outroUserId = souDono ? emp.leitor_user_id : donoId;
      const canceladoPor = souDono ? 'dono' : 'leitor';
      await supabase.from('emprestimos').update({
        status: 'cancelado',
        cancelado_por: canceladoPor,
        cancelamento_justificativa: cancelJustificativa || null,
      }).eq('id', emp.id);
      if (outroUserId) {
        await supabase.from('notificacoes').insert({
          user_id: outroUserId, tipo: 'emprestimo_cancelado',
          emprestimo_id: emp.id, lida: false,
          meta: { cancelado_por: canceladoPor, justificativa: cancelJustificativa || null, livro_titulo: emp.livros?.titulo },
        });
      }
    }
    await supabase.from('notificacoes').update({ lida: true }).eq('id', notif.id);
    setSalvandoCancelamento(false);
    setCancelamentoAberto(null);
    setCancelJustificativa('');
    showToast('Emprestimo cancelado.', 'info');
    buscarNotificacoes();
  }

  async function marcarLida(id) {
    await supabase.from('notificacoes').update({ lida: true }).eq('id', id);
    buscarNotificacoes();
  }

  async function handleAceitarConviteClube(notif) {
    const meta = notif.meta || {};
    const key = `${notif.id}_aceitar`;
    setProc(key, true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const status = meta.clube_publico ? 'aprovado' : 'pendente';
      const { data: existing } = await supabase.from('clube_membros')
        .select('id').eq('clube_id', meta.clube_id).eq('user_id', user.id).maybeSingle();
      if (!existing) {
        await supabase.from('clube_membros').insert({
          clube_id: meta.clube_id,
          user_id: user.id,
          status,
          role: 'membro',
        });
      }
      await supabase.from('notificacoes').update({ lida: true }).eq('id', notif.id);
    } finally {
      setProc(key, false);
      buscarNotificacoes();
    }
  }

  async function handleRecusarConviteClube(notif) {
    const key = `${notif.id}_recusar`;
    setProc(key, true);
    await supabase.from('notificacoes').update({ lida: true }).eq('id', notif.id);
    setProc(key, false);
    buscarNotificacoes();
  }

  function abrirAvaliacao(notif) {
    setAvaliacaoAberta(notif);
    setEstrelasLivro(notif.emprestimos?.leitor_feedback_estrelas || 0);
    setComentarioLivro(notif.emprestimos?.leitor_feedback_comentario || '');
    setEstrelasDono(notif.emprestimos?.avaliacao_dono_estrelas || 0);
    setComentarioDono(notif.emprestimos?.avaliacao_dono_comentario || '');
  }

  async function handleSalvarAvaliacao() {
    if (!avaliacaoAberta) return;
    setSalvando(true);
    await supabase.from('emprestimos').update({
      leitor_feedback_estrelas: estrelasLivro || null,
      leitor_feedback_comentario: comentarioLivro || null,
      avaliacao_dono_estrelas: estrelasDono || null,
      avaliacao_dono_comentario: comentarioDono || null,
    }).eq('id', avaliacaoAberta.emprestimo_id);
    await supabase.from('notificacoes').update({ lida: true }).eq('id', avaliacaoAberta.id);
    setSalvando(false);
    setAvaliacaoAberta(null);
    buscarNotificacoes();
  }

  function jaAvaliou(n) {
    return n.emprestimos?.leitor_feedback_estrelas || n.emprestimos?.leitor_feedback_comentario ||
           n.emprestimos?.avaliacao_dono_estrelas || n.emprestimos?.avaliacao_dono_comentario;
  }

  function abrirAvaliacaoLeitor(notif) {
    setAvaliacaoLeitorAberta(notif);
    setEstrelasLeitorAv(notif.emprestimos?.avaliacao_leitor_estrelas || 0);
    setComentarioLeitorAv(notif.emprestimos?.avaliacao_leitor_comentario || '');
  }

  async function handleSalvarAvaliacaoLeitor() {
    if (!avaliacaoLeitorAberta) return;
    setSalvando(true);
    await supabase.from('emprestimos').update({
      avaliacao_leitor_estrelas: estrelasLeitorAv || null,
      avaliacao_leitor_comentario: comentarioLeitorAv || null,
    }).eq('id', avaliacaoLeitorAberta.emprestimo_id);
    await supabase.from('notificacoes').update({ lida: true }).eq('id', avaliacaoLeitorAberta.id);
    setSalvando(false);
    setAvaliacaoLeitorAberta(null);
    buscarNotificacoes();
  }

  function jaAvaliouLeitor(n) {
    return n.emprestimos?.avaliacao_leitor_estrelas || n.emprestimos?.avaliacao_leitor_comentario;
  }

  const [abaAtiva, setAbaAtiva] = useState('pendencias');

  // Tipos que exigem ação do usuário — só somem ao resolver
  const TIPOS_PENDENCIA = new Set([
    'nova_solicitacao', 'emprestimo_aprovado', 'emprestimo_registrado_dono',
    'leitor_diz_devolveu', 'avaliar_livro', 'avaliar_leitor',
    'vencimento_hoje', 'vencimento_atrasado', 'lembrete_dono',
    'entrega_aguardando', 'devolucao_aguardando',
  ]);

  const pendencias = notificacoes.filter(n => !n.lida && TIPOS_PENDENCIA.has(n.tipo));
  const atualizacoes = notificacoes.filter(n => !TIPOS_PENDENCIA.has(n.tipo));

  const totalPendencias = pendencias.length + solicitacoesClube.length;
  const totalAtualizacoesNaoLidas = atualizacoes.filter(n => !n.lida).length + minhasSolicitacoes.length;

  function cardClass(naoLida) {
    if (naoLida) return 'border rounded-[14px] p-[14px] mb-3 bg-[#F2EEFE] border-[#CFC5F7]';
    return 'border rounded-[14px] p-[14px] mb-3 bg-white border-[#EBE8F0]';
  }

  function renderCabecalho(tipo, titulo, subtitulo, timestamp, urgente) {
    const icStyle = urgente ? { background: '#FEE2E2' } : { background: '#EDE8FC' };
    return (
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex gap-3 flex-1 min-w-0">
          <div className="w-[38px] h-[38px] rounded-[11px] flex items-center justify-center flex-shrink-0"
            style={icStyle}>
            {tipoIcone(tipo, urgente)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13.5px] font-semibold text-ink-900 leading-snug">{titulo}</p>
            {subtitulo && <p className="text-[11.5px] font-semibold text-ink-400 mt-[3px]">{subtitulo}</p>}
          </div>
        </div>
        {timestamp && <span className="text-[11.5px] font-semibold text-ink-300 flex-shrink-0 mt-[2px]">{tempoRelativo(timestamp)}</span>}
      </div>
    );
  }

  function renderNotif(n) {
    // nova_solicitacao — dono recebe, aprova/recusa
    if (n.tipo === 'nova_solicitacao') {
      const emp = n.emprestimos;
      const meta = n.meta || {};
      const aprovar = processando.has(`${n.id}_aprovar`);
      const recusar = processando.has(`${n.id}_recusar`);
      const tituloLivro = emp?.livros?.titulo || meta.livro_titulo || 'Livro';
      const leitorNome = meta.leitor_nome || 'Alguém';
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, tituloLivro, `${leitorNome} quer pegar emprestado`, n.created_at)}
          {emp?.mensagem && (
            <p className="text-xs text-ink-500 italic bg-paper rounded-lg px-3 py-2 mb-3">"{emp.mensagem}"</p>
          )}
          {emp?.prazo_dias && (
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs text-ink-400">Prazo: {emp.prazo_dias} dias</span>
            </div>
          )}
          {!n.lida && emp && (() => {
            const rep = repLeitoresMap[emp.leitor_user_id];
            if (!rep) return null;
            return (
              <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-xl flex-wrap" style={{background:'#F7F5FF'}}>
                {rep.media && (
                  <span className="flex items-center gap-1 text-[12px] font-bold flex-shrink-0" style={{color:'#9A6B12'}}>
                    <span style={{color:'#FFAA00'}}>★</span>
                    {rep.media}
                    <span className="font-semibold text-ink-400">({rep.total})</span>
                  </span>
                )}
                {rep.badges.slice(0, 2).map(tipo => {
                  const def = BADGE_DEFS.find(d => d.tipo === tipo);
                  if (!def) return null;
                  return (
                    <span key={tipo} className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${def.cor}`}>
                      {def.Icon && <def.Icon size={10} />}
                      {def.nome}
                    </span>
                  );
                })}
              </div>
            );
          })()}
          {!n.lida && emp && repLeitoresMap[emp.leitor_user_id]?.bloqueado && (
            <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl mb-3" style={{background:'#FEF2F2', border:'1px solid #FECACA'}}>
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <span className="text-[11.5px] font-bold" style={{color:'#DC2626'}}>Este leitor esta inadimplente em outro emprestimo</span>
            </div>
          )}
          {!n.lida && emp && (
            <div className="flex gap-2">
              <button onClick={() => handleAprovar(n)} disabled={aprovar || recusar}
                className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors disabled:opacity-50">
                {aprovar ? 'Aprovando...' : 'Aprovar'}
              </button>
              <button onClick={() => handleRecusar(n)} disabled={aprovar || recusar}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors disabled:opacity-50">
                {recusar ? 'Recusando...' : 'Recusar'}
              </button>
            </div>
          )}
          {!n.lida && !emp && (
            <p className="text-xs text-ink-300">Solicitação recebida — acesse Acervo para responder.</p>
          )}
          {n.lida && <p className="text-xs text-ink-300">Respondido</p>}
        </div>
      );
    }

    // emprestimo_registrado_dono — leitor é informado que dono registrou o empréstimo direto
    if (n.tipo === 'emprestimo_registrado_dono') {
      const meta = n.meta || {};
      const confirmar = processando.has(`${n.id}_confirmar`);
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, meta.livro_titulo || 'Livro', `${meta.dono_nome || 'Alguem'} registrou que te emprestou este livro`, n.created_at)}
          {!n.lida && (
            <div className="space-y-2">
              <button onClick={async () => {
                setProc(`${n.id}_confirmar`, true);
                await supabase.from('notificacoes').update({ lida: true }).eq('id', n.id);
                showToast('Recebimento confirmado!', 'success');
                buscarNotificacoes();
              }} disabled={confirmar}
                className="w-full py-[11px] rounded-[12px] text-[13px] font-extrabold text-white border-0 cursor-pointer disabled:opacity-50"
                style={{background:'#1FA971'}}>
                {confirmar ? 'Confirmando...' : 'Confirmar que recebi'}
              </button>
              <button onClick={() => marcarLida(n.id)}
                className="w-full py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Ignorar
              </button>
            </div>
          )}
          {n.lida && <p className="text-xs text-ink-300">Recebimento confirmado</p>}
        </div>
      );
    }

    // emprestimo_aprovado — leitor vê WhatsApp do dono e combina entrega
    if (n.tipo === 'emprestimo_aprovado') {
      const emp = n.emprestimos;
      const meta = n.meta || {};
      const donoWhatsapp = meta.dono_whatsapp;
      const waLink = donoWhatsapp ? `https://wa.me/55${donoWhatsapp.replace(/\D/g, '')}` : null;
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, emp?.livros?.titulo || 'Livro', 'Aprovado! Combine a entrega pelo WhatsApp.', n.created_at)}
          {!n.lida && (
            <div className="space-y-2">
              {waLink ? (
                <a href={waLink} target="_blank" rel="noopener noreferrer"
                  className="w-full flex items-center justify-between gap-3 py-3 px-4 rounded-xl no-underline"
                  style={{background:'#F0FEF4', border:'1px solid #C6F1DB'}}
                  onClick={() => marcarLida(n.id)}>
                  <div>
                    <p className="text-xs font-bold text-ink-900 mb-0.5">WhatsApp do dono</p>
                    <p className="text-xs text-ink-500">{donoWhatsapp}</p>
                  </div>
                  <span className="px-3 py-1.5 rounded-[10px] text-xs font-bold text-white flex-shrink-0"
                    style={{background:'#25D366'}}>
                    Abrir
                  </span>
                </a>
              ) : (
                <p className="text-xs text-ink-400 bg-paper rounded-xl px-3 py-2">
                  O dono ainda nao cadastrou o WhatsApp. Acesse o Acervo para confirmar a entrega.
                </p>
              )}
              <p className="text-xs text-ink-400">
                Confirme a entrega no seu Acervo apos receber o livro.
              </p>
              <button onClick={() => marcarLida(n.id)}
                className="w-full py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Ok, entendi
              </button>
            </div>
          )}
          {n.lida && <p className="text-xs text-ink-300">Emprestimo aprovado</p>}
        </div>
      );
    }

    // entrega_proposta / entrega_agendada / entrega_combinada — tipos legados
    if (['entrega_proposta', 'entrega_agendada', 'entrega_combinada'].includes(n.tipo)) {
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, n.emprestimos?.livros?.titulo || 'Livro', 'Entrega combinada — confirme no Acervo.', n.created_at)}
          {!n.lida && (
            <button onClick={() => marcarLida(n.id)}
              className="w-full py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
              Ok, entendi
            </button>
          )}
        </div>
      );
    }

    // emprestimo_recusado — leitor é informado
    if (n.tipo === 'emprestimo_recusado') {
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, n.emprestimos?.livros?.titulo || 'Livro', 'Seu pedido de empréstimo não foi aprovado.', n.created_at)}
          {!n.lida && (
            <button onClick={() => marcarLida(n.id)}
              className="w-full py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
              Marcar como lida
            </button>
          )}
        </div>
      );
    }

    // avaliar_livro — leitor avalia
    if (n.tipo === 'avaliar_livro') {
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, n.emprestimos?.livros?.titulo || 'Livro', 'Você devolveu esse livro. Que tal avaliar?', n.created_at)}
          {jaAvaliou(n) && (
            <div className="flex flex-col gap-2 mb-3">
              {(n.emprestimos?.leitor_feedback_estrelas || n.emprestimos?.leitor_feedback_comentario) && (
                <div className="bg-paper rounded-lg p-3">
                  <p className="text-xs text-ink-400 mb-1">Avaliação do livro</p>
                  {n.emprestimos?.leitor_feedback_estrelas > 0 && (
                    <div className="flex gap-0.5 mb-1">
                      {[1,2,3,4,5].map(i => (
                        <span key={i} className={`text-xs ${i <= n.emprestimos.leitor_feedback_estrelas ? 'text-amber-400' : 'text-ink-300'}`}>★</span>
                      ))}
                    </div>
                  )}
                  {n.emprestimos?.leitor_feedback_comentario && (
                    <p className="text-xs text-ink-500 italic">"{n.emprestimos.leitor_feedback_comentario}"</p>
                  )}
                </div>
              )}
              {(n.emprestimos?.avaliacao_dono_estrelas || n.emprestimos?.avaliacao_dono_comentario) && (
                <div className="bg-paper rounded-lg p-3">
                  <p className="text-xs text-ink-400 mb-1">Avaliação do dono</p>
                  {n.emprestimos?.avaliacao_dono_estrelas > 0 && (
                    <div className="flex gap-0.5 mb-1">
                      {[1,2,3,4,5].map(i => (
                        <span key={i} className={`text-xs ${i <= n.emprestimos.avaliacao_dono_estrelas ? 'text-amber-400' : 'text-ink-300'}`}>★</span>
                      ))}
                    </div>
                  )}
                  {n.emprestimos?.avaliacao_dono_comentario && (
                    <p className="text-xs text-ink-500 italic">"{n.emprestimos.avaliacao_dono_comentario}"</p>
                  )}
                </div>
              )}
            </div>
          )}
          {!jaAvaliou(n) && (
            <div className="flex gap-2">
              <button onClick={() => abrirAvaliacao(n)}
                className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors">
                Avaliar experiência
              </button>
              <button onClick={async () => {
                await supabase.from('notificacoes').update({ lida: true }).eq('id', n.id);
                buscarNotificacoes();
              }}
                className="flex-shrink-0 py-2 px-3 text-xs border border-line text-ink-400 rounded-lg hover:bg-paper transition-colors">
                Não quero avaliar
              </button>
            </div>
          )}
        </div>
      );
    }

    // avaliar_leitor — dono avalia o leitor
    if (n.tipo === 'avaliar_leitor') {
      const titulo = n.emprestimos?.livros?.titulo || 'Livro';
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, titulo, 'Como foi o leitor? Deixe uma avaliação.', n.created_at)}
          {jaAvaliouLeitor(n) && (
            <div className="bg-paper rounded-lg p-3 mb-3">
              <p className="text-xs text-ink-400 mb-1">Sua avaliação do leitor</p>
              {n.emprestimos?.avaliacao_leitor_estrelas > 0 && (
                <div className="flex gap-0.5 mb-1">
                  {[1,2,3,4,5].map(i => (
                    <span key={i} className={`text-xs ${i <= n.emprestimos.avaliacao_leitor_estrelas ? 'text-amber-400' : 'text-ink-300'}`}>★</span>
                  ))}
                </div>
              )}
              {n.emprestimos?.avaliacao_leitor_comentario && (
                <p className="text-xs text-ink-500 italic">"{n.emprestimos.avaliacao_leitor_comentario}"</p>
              )}
            </div>
          )}
          {!jaAvaliouLeitor(n) && (
            <div className="flex gap-2">
              <button onClick={() => abrirAvaliacaoLeitor(n)}
                className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors">
                Avaliar leitor
              </button>
              <button onClick={async () => {
                await supabase.from('notificacoes').update({ lida: true }).eq('id', n.id);
                buscarNotificacoes();
              }}
                className="flex-shrink-0 py-2 px-3 text-xs border border-line text-ink-400 rounded-lg hover:bg-paper transition-colors">
                Não quero avaliar
              </button>
            </div>
          )}
        </div>
      );
    }

    // vencimento / lembrete_dono
    if (n.tipo === 'vencimento_hoje' || n.tipo === 'vencimento_atrasado' || n.tipo === 'lembrete_dono') {
      const isAtrasado = n.tipo === 'vencimento_atrasado';
      const titulo = n.emprestimos?.livros?.titulo;
      const msg = isAtrasado
        ? 'Devolucao atrasada — entre em contato pelo WhatsApp e confirme no Acervo.'
        : n.tipo === 'lembrete_dono'
          ? 'O leitor ainda nao confirmou a devolucao — entre em contato pelo WhatsApp.'
          : 'Hoje e o dia de devolver — combine pelo WhatsApp e confirme no Acervo.';
      return (
        <div key={n.id} className={`border rounded-[14px] p-[14px] mb-3 ${isAtrasado ? 'bg-[#FEF2F2] border-[#FECACA]' : !n.lida ? 'bg-[#FFFBEB] border-[#FDE68A]' : 'bg-white border-[#EBE8F0]'}`}>
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex gap-3 flex-1 min-w-0">
              <div className="w-[38px] h-[38px] rounded-[11px] flex items-center justify-center text-[17px] flex-shrink-0"
                style={isAtrasado ? {background:'#FEE2E2',color:'#DC2626'} : {background:'#FBF1DC',color:'#9A6B12'}}>
                !
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-ink-900 truncate">{titulo}</p>
                <p className={`text-xs mt-0.5 ${isAtrasado ? 'text-red-500' : 'text-amber-600'}`}>{msg}</p>
              </div>
            </div>
            <span className="text-xs text-ink-300 flex-shrink-0">{tempoRelativo(n.created_at)}</span>
          </div>
          {!n.lida && (
            <button onClick={() => marcarLida(n.id)}
              className="w-full py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
              Ok, entendi
            </button>
          )}
        </div>
      );
    }

    // leitor_diz_devolveu — dono confirma recebimento
    if (n.tipo === 'leitor_diz_devolveu') {
      const confirmando = processando.has(`${n.id}_confirmar`);
      const leitorNomeDev = n.meta?.leitor_nome || n.emprestimos?.leitor_nome || 'Leitor';
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, n.emprestimos?.livros?.titulo || 'Livro',
            `${leitorNomeDev} diz que devolveu.`, n.created_at)}
          {!n.lida && (
            <div className="flex gap-2">
              <button onClick={() => handleConfirmarDevolucao(n)} disabled={confirmando}
                className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors disabled:opacity-50">
                {confirmando ? 'Confirmando...' : 'Confirmar devolução'}
              </button>
              <button onClick={() => marcarLida(n.id)}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Depois
              </button>
            </div>
          )}
          {n.lida && <p className="text-xs text-ink-300">Devolução confirmada</p>}
        </div>
      );
    }

    // leitor_avisou_atraso — dono é informado
    if (n.tipo === 'leitor_avisou_atraso') {
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, n.emprestimos?.livros?.titulo || 'Livro',
            `${n.emprestimos?.leitor_nome} avisou que vai atrasar.`, n.created_at, true)}
          {n.emprestimos?.nova_data_devolucao && (
            <p className="text-xs text-ink-400 mb-3">
              Nova data solicitada: {new Date(n.emprestimos.nova_data_devolucao + 'T12:00:00').toLocaleDateString('pt-BR')}
            </p>
          )}
          {n.emprestimos?.motivo_atraso && (
            <p className="text-xs text-ink-500 italic mb-3">"{n.emprestimos.motivo_atraso}"</p>
          )}
          {!n.lida && (
            <div className="flex gap-2">
              {onVoltar && (
                <button onClick={() => { marcarLida(n.id); onVoltar(); }}
                  className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors">
                  Ver no acervo
                </button>
              )}
              <button onClick={() => marcarLida(n.id)}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Ok
              </button>
            </div>
          )}
        </div>
      );
    }

    // livro_entregue — confirmação de entrega física
    if (n.tipo === 'livro_entregue') {
      const meta = n.meta || {};
      const titulo = meta.livro_titulo || n.emprestimos?.livros?.titulo || 'Livro';
      const confirmadoPor = meta.confirmado_por === 'leitor' ? 'O leitor confirmou que recebeu o livro.' : 'O dono confirmou que entregou o livro.';
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, titulo, confirmadoPor, n.created_at)}
          {!n.lida && (
            <div className="flex gap-2">
              {onVoltar && (
                <button onClick={() => { marcarLida(n.id); onVoltar(); }}
                  className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors">
                  Ver no acervo
                </button>
              )}
              <button onClick={() => marcarLida(n.id)}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Ok
              </button>
            </div>
          )}
        </div>
      );
    }

    // livro_disponivel — leitor é notificado
    if (n.tipo === 'livro_disponivel') {
      const donoUserId = n.emprestimos?.livros?.user_id;
      const perfil = donoUserId ? perfilDonos[donoUserId] : null;
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, n.emprestimos?.livros?.titulo || 'Livro',
            `Disponível na biblioteca de ${perfil?.nome || 'um amigo'}!`, n.created_at)}
          <div className="flex gap-2">
            {perfil?.slug && (
              <button onClick={() => navigate(`/biblioteca/${perfil.slug}`)}
                className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors">
                Ver biblioteca →
              </button>
            )}
            {!n.lida && (
              <button onClick={() => marcarLida(n.id)}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Marcar como lida
              </button>
            )}
          </div>
        </div>
      );
    }

    // clube_aprovado — membro é notificado
    if (n.tipo === 'clube_aprovado') {
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, 'Entrada no clube aprovada!', 'Acesse o clube para adicionar seus livros ao acervo.', n.created_at)}
          {!n.lida && (
            <div className="flex gap-2">
              {onIrParaClubes && (
                <button onClick={() => { marcarLida(n.id); onIrParaClubes(); }}
                  className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors">
                  Ir para meus clubes →
                </button>
              )}
              <button onClick={() => marcarLida(n.id)}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Depois
              </button>
            </div>
          )}
        </div>
      );
    }

    // clube_rejeitado — membro é notificado
    if (n.tipo === 'clube_rejeitado') {
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, 'Solicitação não aprovada', 'Sua entrada em um clube não foi aprovada.', n.created_at)}
          {!n.lida && (
            <button onClick={() => marcarLida(n.id)}
              className="w-full py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
              Marcar como lida
            </button>
          )}
        </div>
      );
    }

    // novo_seguidor
    if (n.tipo === 'novo_seguidor') {
      const meta = n.meta || {};
      const perfil = perfilSeguidores[meta.seguidor_id] || {};
      const slug = perfil.slug;
      const jasSigo = jaSigoBack.has(meta.seguidor_id);
      const seguindo = seguindoBack.has(meta.seguidor_id);
      const sub = meta.username ? `@${meta.username} começou a seguir você` : 'começou a seguir você';
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, meta.nome || 'Alguém', sub, n.created_at)}
          <div className="flex gap-2 mt-1">
            {slug && (
              <button onClick={() => navigate(`/biblioteca/${slug}`)}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Ver perfil
              </button>
            )}
            {jasSigo ? (
              <div className="flex-1 py-2 text-xs text-center text-green-600 bg-green-50 rounded-lg">
                Seguindo
              </div>
            ) : (
              <button onClick={() => handleSeguirBack(meta.seguidor_id, !n.lida ? n.id : null)}
                disabled={seguindo}
                className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors disabled:opacity-50">
                {seguindo ? 'Seguindo...' : 'Seguir de volta'}
              </button>
            )}
          </div>
        </div>
      );
    }

    // novo_membro_clube
    if (n.tipo === 'novo_membro_clube') {
      const meta = n.meta || {};
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, meta.nome_membro || 'Novo membro',
            `entrou no clube ${meta.nome_clube || ''}`, n.created_at)}
          {!n.lida && (
            <div className="flex gap-2">
              {onIrParaClubes && (
                <button onClick={() => { marcarLida(n.id); onIrParaClubes(meta.clube_id); }}
                  className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors">
                  Ver clube →
                </button>
              )}
              <button onClick={() => marcarLida(n.id)}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Marcar como lida
              </button>
            </div>
          )}
        </div>
      );
    }

    // novo_livro_clube
    if (n.tipo === 'novo_livro_clube') {
      const meta = n.meta || {};
      const qtd = meta.quantidade || 0;
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, meta.nome_adicionador || 'Membro',
            `adicionou ${qtd} livro${qtd !== 1 ? 's' : ''} ao clube ${meta.nome_clube || ''}`, n.created_at)}
          {!n.lida && (
            <div className="flex gap-2">
              {onIrParaClubes && (
                <button onClick={() => { marcarLida(n.id); onIrParaClubes(meta.clube_id); }}
                  className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors">
                  Ver clube →
                </button>
              )}
              <button onClick={() => marcarLida(n.id)}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Marcar como lida
              </button>
            </div>
          )}
        </div>
      );
    }

    // mensagem_clube — aviso do admin para todos os membros
    if (n.tipo === 'mensagem_clube') {
      const meta = n.meta || {};
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, meta.clube_nome || 'Clube',
            meta.remetente ? `Aviso de ${meta.remetente}` : 'Aviso do administrador', n.created_at)}
          {meta.mensagem && (
            <p className="text-[13px] text-ink-700 bg-paper rounded-lg px-3 py-2.5 mt-1 leading-snug">
              {meta.mensagem}
            </p>
          )}
          {!n.lida && (
            <div className="flex gap-2 mt-2">
              {onIrParaClubes && (
                <button onClick={() => { marcarLida(n.id); onIrParaClubes(meta.clube_id); }}
                  className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors">
                  Ver clube →
                </button>
              )}
              <button onClick={() => marcarLida(n.id)}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Ok
              </button>
            </div>
          )}
        </div>
      );
    }

    // convite_clube — usuário recebeu convite para entrar num clube
    if (n.tipo === 'convite_clube') {
      const meta = n.meta || {};
      const aceitar = processando.has(`${n.id}_aceitar`);
      const recusar = processando.has(`${n.id}_recusar`);
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, meta.clube_nome || 'Clube',
            `${meta.convidado_por_nome || 'Alguém'} te convidou para participar`, n.created_at)}
          {!n.lida && (
            <div className="flex gap-2">
              <button onClick={() => handleAceitarConviteClube(n)} disabled={aceitar || recusar}
                className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors disabled:opacity-50">
                {aceitar ? 'Entrando...' : 'Aceitar'}
              </button>
              <button onClick={() => handleRecusarConviteClube(n)} disabled={aceitar || recusar}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors disabled:opacity-50">
                {recusar ? 'Recusando...' : 'Recusar'}
              </button>
            </div>
          )}
        </div>
      );
    }

    // novo_interesse — dono recebe quando alguém dá swipe direita no seu livro
    if (n.tipo === 'novo_interesse') {
      const meta = n.meta || {};
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, meta.leitor_nome || 'Alguém',
            `tem interesse em "${meta.livro_titulo || 'seu livro'}"`, n.created_at)}
          {!n.lida && (
            <button onClick={() => marcarLida(n.id)}
              className="w-full py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
              Marcar como lida
            </button>
          )}
        </div>
      );
    }

    // proposta_dono — leitor recebe quando dono clica "Oferecer"
    if (n.tipo === 'proposta_dono') {
      const meta = n.meta || {};
      const solicitando = processando.has(`${n.id}_solicitar`);
      async function handleSolicitarDeProposta() {
        if (!meta.dono_id || !meta.livro_id) return;
        setProcessando(prev => new Set([...prev, `${n.id}_solicitar`]));
        const { data: { user } } = await supabase.auth.getUser();
        const { data: empData } = await supabase.from('emprestimos').insert({
          livro_id: meta.livro_id, leitor_user_id: user.id, status: 'pendente',
        }).select('id').single();
        await supabase.from('notificacoes').insert({
          user_id: meta.dono_id, tipo: 'nova_solicitacao', livro_id: meta.livro_id, lida: false,
          emprestimo_id: empData?.id,
          meta: { livro_titulo: meta.livro_titulo, leitor_nome: user.user_metadata?.nome || 'Alguém' },
        });
        await marcarLida(n.id);
        setProcessando(prev => { const s = new Set(prev); s.delete(`${n.id}_solicitar`); return s; });
        showToast('Solicitação enviada!', 'success');
      }
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, meta.dono_nome || 'Alguém',
            `quer te emprestar "${meta.livro_titulo || 'um livro'}"`, n.created_at)}
          {!n.lida && (
            <div className="flex gap-2">
              <button onClick={handleSolicitarDeProposta} disabled={solicitando}
                className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors disabled:opacity-50">
                {solicitando ? 'Enviando…' : 'Solicitar empréstimo →'}
              </button>
              <button onClick={() => marcarLida(n.id)}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Ignorar
              </button>
            </div>
          )}
        </div>
      );
    }

    // ja_tenho_livro — dono é informado que leitor já tinha o livro
    if (n.tipo === 'ja_tenho_livro') {
      const meta = n.meta || {};
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, meta.titulo || 'Livro', 'O leitor já está com o livro. Emprestimo ativo.', n.created_at)}
          {!n.lida && (
            <button onClick={() => marcarLida(n.id)}
              className="w-full py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
              Marcar como lida
            </button>
          )}
        </div>
      );
    }

    // devolucao_iniciar_pessoalmente / proposta_devolucao / devolucao_agendada — tipos legados
    if (['devolucao_iniciar_pessoalmente', 'proposta_devolucao', 'devolucao_agendada'].includes(n.tipo)) {
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, n.emprestimos?.livros?.titulo || 'Livro', 'Devolucao — confirme no Acervo.', n.created_at)}
          {!n.lida && (
            <button onClick={() => marcarLida(n.id)}
              className="w-full py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
              Ok, entendi
            </button>
          )}
        </div>
      );
    }

    // entrega_aguardando — aguardando confirmacao do outro lado
    if (n.tipo === 'entrega_aguardando') {
      const meta = n.meta || {};
      const quemConfirmou = meta.confirmado_por === 'dono' ? 'O dono confirmou que entregou' : 'Voce confirmou que recebeu';
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, meta.livro_titulo || 'Livro', `${quemConfirmou} — confirme no Acervo.`, n.created_at)}
          {!n.lida && (
            <div className="flex gap-2">
              {onVoltar && (
                <button onClick={() => { marcarLida(n.id); onVoltar(); }}
                  className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors">
                  Ir para o acervo
                </button>
              )}
              <button onClick={() => marcarLida(n.id)}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Ok
              </button>
            </div>
          )}
        </div>
      );
    }

    // devolucao_aguardando — aguardando confirmacao do outro lado
    if (n.tipo === 'devolucao_aguardando') {
      const meta = n.meta || {};
      const quemConfirmou = meta.confirmado_por === 'dono' ? 'O dono quer confirmar a devolucao' : 'Voce disse que devolveu';
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, meta.livro_titulo || 'Livro', `${quemConfirmou} — confirme no Acervo.`, n.created_at)}
          {!n.lida && (
            <div className="flex gap-2">
              {onVoltar && (
                <button onClick={() => { marcarLida(n.id); onVoltar(); }}
                  className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors">
                  Ir para o acervo
                </button>
              )}
              <button onClick={() => marcarLida(n.id)}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Ok
              </button>
            </div>
          )}
        </div>
      );
    }

    // emprestimo_inadimplente
    if (n.tipo === 'emprestimo_inadimplente') {
      const titulo = n.emprestimos?.livros?.titulo || 'Livro';
      const isDono = n.meta?.papel === 'dono';
      const confirmando = processando.has(`${n.id}_resolver`);
      return (
        <div key={n.id} className="border rounded-[14px] p-[14px] mb-3 bg-[#FEF2F2] border-[#FECACA]">
          {renderCabecalho(n.tipo, titulo,
            isDono ? 'O leitor não devolveu no prazo. Quando receber, confirme abaixo.' : 'Voce esta com um emprestimo em atraso. Devolva o livro para desbloquear sua conta.',
            n.created_at, true)}
          {!n.lida && isDono && (
            <button onClick={() => handleResolverInadimplente(n)} disabled={confirmando}
              className="w-full py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors disabled:opacity-50">
              {confirmando ? 'Processando...' : 'Confirmar que recebi o livro'}
            </button>
          )}
          {!n.lida && !isDono && (
            <button onClick={() => marcarLida(n.id)}
              className="w-full py-2 text-xs border border-red-200 text-red-500 rounded-lg hover:bg-red-50 transition-colors">
              Ok, entendi
            </button>
          )}
        </div>
      );
    }

    // emprestimo_cancelado — outra parte cancela o empréstimo
    if (n.tipo === 'emprestimo_cancelado') {
      const meta = n.meta || {};
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, meta.livro_titulo || 'Livro',
            `empréstimo cancelado por ${meta.cancelado_por === 'dono' ? 'o dono' : 'o leitor'}${meta.justificativa ? `: ${meta.justificativa}` : ''}`, n.created_at)}
          {!n.lida && (
            <button onClick={() => marcarLida(n.id)}
              className="w-full py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
              Marcar como lida
            </button>
          )}
        </div>
      );
    }

    // solicitacao_expirada — pedido sem resposta após 3 dias
    if (n.tipo === 'solicitacao_expirada') {
      const meta = n.meta || {};
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, meta.titulo || 'Livro',
            'Solicitação expirada — sem resposta após 3 dias.', n.created_at)}
          {!n.lida && (
            <button onClick={() => marcarLida(n.id)}
              className="w-full py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
              Ok, entendi
            </button>
          )}
        </div>
      );
    }

    // novo_clube_amigo — amigo cria um clube
    if (n.tipo === 'novo_clube_amigo') {
      const meta = n.meta || {};
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, meta.criador_nome || 'Alguém',
            `criou o clube "${meta.clube_nome || 'novo clube'}"`, n.created_at)}
          {!n.lida && (
            <div className="flex gap-2">
              {onIrParaClubes && meta.clube_id && (
                <button onClick={() => { marcarLida(n.id); onIrParaClubes(meta.clube_id); }}
                  className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors">
                  Ver clube →
                </button>
              )}
              <button onClick={() => marcarLida(n.id)}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Marcar como lida
              </button>
            </div>
          )}
        </div>
      );
    }

    // solicitacao_devolucao — dono solicitou devolução de empréstimo indeterminado
    if (n.tipo === 'solicitacao_devolucao') {
      const meta = n.meta || {};
      const titulo = meta.livro_titulo || n.emprestimos?.livros?.titulo || 'Livro';
      const dataLimite = meta.data_devolucao
        ? new Date(meta.data_devolucao + 'T12:00:00').toLocaleDateString('pt-BR') : null;
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, 'Devolução solicitada',
            `O dono pediu de volta "${titulo}"${dataLimite ? ` — prazo: ${dataLimite}` : ''}`,
            n.created_at)}
          {!n.lida && (
            <div className="flex gap-2">
              {onVoltar && (
                <button onClick={() => { marcarLida(n.id); onVoltar(); }}
                  className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors">
                  Ver no acervo
                </button>
              )}
              <button onClick={() => marcarLida(n.id)}
                className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                Ok
              </button>
            </div>
          )}
        </div>
      );
    }

    // devolucao_enviada — dono recebe aviso que leitor postou o livro de volta
    if (n.tipo === 'devolucao_enviada') {
      const meta = n.meta || {};
      const titulo = meta.titulo || n.emprestimos?.livros?.titulo || 'Livro';
      return (
        <div key={n.id} className={cardClass(!n.lida)}>
          {renderCabecalho(n.tipo, 'Devolução enviada',
            `${meta.leitor_nome || 'O leitor'} postou "${titulo}" de volta${meta.codigo ? ` — rastreio: ${meta.codigo}` : ''}`,
            n.created_at)}
          {meta.codigo && (
            <div className="bg-paper rounded-xl p-3 mb-3">
              <p className="text-xs font-semibold text-ink-700 mb-1">Código de rastreio</p>
              <p className="text-sm font-mono font-bold text-ink-900 tracking-widest">{meta.codigo}</p>
            </div>
          )}
          {!n.lida && (
            <button onClick={() => marcarLida(n.id)}
              className="w-full py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
              Marcar como lida
            </button>
          )}
        </div>
      );
    }

    return null;
  }

  function renderSolicitacaoClube(membro) {
    const perfil = perfisSolicitantes[membro.user_id];
    const nomeClube = membro.clubes?.nome || 'Clube';
    const aprovar = processando.has(`${membro.id}_aprovar`);
    const rejeitar = processando.has(`${membro.id}_rejeitar`);
    return (
      <div key={membro.id} className="border rounded-[14px] p-[14px] mb-3 bg-[#F2EEFE] border-[#CFC5F7]">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex gap-3 flex-1 min-w-0">
            <div className="w-[38px] h-[38px] rounded-[11px] flex items-center justify-center flex-shrink-0" style={{background:'#FFE8E3'}}>
              <Users size={17} color="#FF5C8A" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-ink-900 truncate">{perfil?.nome || 'Usuário'}</p>
              <p className="text-xs text-ink-400 mt-0.5">quer entrar no clube <span className="font-medium text-ink-500">{nomeClube}</span></p>
              {perfil?.username && <p className="text-xs text-ink-300 mt-0.5">@{perfil.username}</p>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => handleAprovarMembro(membro)} disabled={aprovar || rejeitar}
            className="flex-1 py-2 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors disabled:opacity-50">
            {aprovar ? 'Aprovando...' : 'Aprovar'}
          </button>
          <button onClick={() => handleRejeitarMembro(membro)} disabled={aprovar || rejeitar}
            className="flex-1 py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors disabled:opacity-50">
            {rejeitar ? 'Rejeitando...' : 'Rejeitar'}
          </button>
        </div>
      </div>
    );
  }

  async function limparTudo() {
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('notificacoes').update({ lida: true }).eq('user_id', user.id).eq('lida', false);
    showToast('Tudo lido', 'success');
    buscarNotificacoes();
  }

  async function marcarAtualizacoesLidas() {
    const naoLidas = atualizacoes.filter(n => !n.lida).map(n => n.id);
    if (naoLidas.length === 0) return;
    await supabase.from('notificacoes').update({ lida: true }).in('id', naoLidas);
    buscarNotificacoes();
  }

  const notifComFluxo = [...pendencias, ...atualizacoes].find(n =>
    n.emprestimos?.livros && ['pendente', 'aprovado', 'em_andamento'].includes(n.emprestimos?.status)
  );

  return (
    <div className="bg-paper font-sans flex flex-col" style={{height:'100dvh'}}>
      {/* Mobile layout */}
      <div className="flex-1 lg:hidden" style={{overflowY:'auto'}}>
      {/* back-header */}
      <div className="sticky top-0 z-10"
        style={{background:'rgba(250,248,245,0.88)', backdropFilter:'blur(14px) saturate(160%)', WebkitBackdropFilter:'blur(14px) saturate(160%)', borderBottom:'1px solid #EBE8F0'}}>
        <div className="flex items-center gap-3 px-[18px] pb-3"
          style={{paddingTop:'calc(env(safe-area-inset-top) + 44px)'}}>
          <button onClick={onVoltar}
            className="w-[38px] h-[38px] rounded-[12px] bg-white border border-[#EBE8F0] flex items-center justify-center flex-shrink-0 text-ink-700">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          </button>
          <h1 className="flex-1 text-[17px] font-extrabold tracking-tight text-ink-900 m-0">Notificações</h1>
        </div>
        {/* Abas */}
        <div className="flex px-[18px] pb-3 gap-2">
          <button onClick={() => setAbaAtiva('pendencias')}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-[11px] text-[13px] font-semibold transition-all"
            style={abaAtiva === 'pendencias'
              ? {background:'#5B3CE4', color:'#fff', boxShadow:'0 2px 8px rgba(91,60,228,0.25)'}
              : {background:'#EDE8F5', color:'#6E6878'}}>
            Pendências
            {totalPendencias > 0 && (
              <span className="text-[11px] font-extrabold px-[7px] py-[1px] rounded-full"
                style={{background: abaAtiva === 'pendencias' ? 'rgba(255,255,255,0.25)' : '#C5462A', color:'#fff'}}>
                {totalPendencias}
              </span>
            )}
          </button>
          <button onClick={() => { setAbaAtiva('atualizacoes'); marcarAtualizacoesLidas(); }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-[11px] text-[13px] font-semibold transition-all"
            style={abaAtiva === 'atualizacoes'
              ? {background:'#5B3CE4', color:'#fff', boxShadow:'0 2px 8px rgba(91,60,228,0.25)'}
              : {background:'#EDE8F5', color:'#6E6878'}}>
            Atualizações
            {totalAtualizacoesNaoLidas > 0 && (
              <span className="text-[11px] font-extrabold px-[7px] py-[1px] rounded-full"
                style={{background: abaAtiva === 'atualizacoes' ? 'rgba(255,255,255,0.25)' : '#5B3CE4', color:'#fff'}}>
                {totalAtualizacoesNaoLidas}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-5 pb-8 pt-4">

        {carregando ? (
          <p className="text-[13px] text-ink-400 text-center mt-12 font-medium">Carregando...</p>
        ) : abaAtiva === 'pendencias' ? (
          totalPendencias === 0 ? (
            <div style={{textAlign:'center', padding:'60px 20px'}}>
              <div style={{width:56,height:56,borderRadius:18,background:'#EDE8FC',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px',fontSize:22,fontWeight:800,color:'#7C5CBF'}}>ok</div>
              <p style={{fontSize:14,fontWeight:700,color:'#211C2B',margin:'0 0 4px'}}>Tudo resolvido</p>
              <p style={{fontSize:12.5,margin:0,color:'#9A93A4'}}>Sem pendências no momento.</p>
            </div>
          ) : (
            <>
              {solicitacoesClube.map(m => renderSolicitacaoClube(m))}
              {pendencias.map(n => renderNotif(n))}
            </>
          )
        ) : (
          atualizacoes.length === 0 && minhasSolicitacoes.length === 0 ? (
            <div style={{textAlign:'center', padding:'60px 20px'}}>
              <div style={{width:56,height:56,borderRadius:18,background:'#EDE8FC',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px'}}>
                <Bell size={24} color="#5B3CE4" />
              </div>
              <p style={{fontSize:14,fontWeight:700,color:'#211C2B',margin:'0 0 4px'}}>Sem atualizações</p>
              <p style={{fontSize:12.5,margin:0,color:'#9A93A4'}}>Nada por aqui ainda.</p>
            </div>
          ) : (
            <>
              {totalAtualizacoesNaoLidas > 0 && (
                <div className="flex justify-end mb-3">
                  <button onClick={limparTudo} className="text-[13px] font-bold border-0 bg-transparent cursor-pointer" style={{color:'#5B3CE4'}}>
                    Limpar tudo
                  </button>
                </div>
              )}
              {minhasSolicitacoes.map(s => (
                <div key={s.id} className="border rounded-[14px] p-[14px] mb-3 bg-white border-[#EBE8F0]">
                  <div className="flex gap-3 mb-3">
                    <div className="w-[38px] h-[38px] rounded-[11px] flex items-center justify-center flex-shrink-0" style={{background:'#FBF1DC'}}>
                      <Users size={17} color="#9A6B12" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-ink-900 truncate">{s.clubes?.nome || 'Clube'}</p>
                      <p className="text-xs text-amber-600 mt-0.5">Aguardando aprovação do admin</p>
                    </div>
                  </div>
                  {onIrParaClubes && (
                    <button onClick={() => onIrParaClubes(s.clube_id)}
                      className="w-full py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                      Ver clube
                    </button>
                  )}
                </div>
              ))}
              {atualizacoes.map(n => renderNotif(n))}
            </>
          )
        )}
      </div>
      </div>{/* end mobile wrapper */}

      {/* Desktop layout */}
      <div className="hidden lg:flex flex-1 overflow-hidden">
        {/* Left: notifications list */}
        <div style={{flex:1,overflowY:'auto',scrollbarWidth:'none',padding:'0 40px'}}>
          <div style={{paddingTop:32,paddingBottom:24}}>
            <p style={{fontSize:10,fontWeight:800,letterSpacing:'.15em',textTransform:'uppercase',color:'#5B3CE4',margin:'0 0 8px'}}>NOTIFICAÇÕES</p>
            <h1 style={{fontSize:36,fontWeight:800,letterSpacing:'-.03em',color:'#211C2B',margin:'0 0 24px',lineHeight:1.1}}>Sua caixa de entrada</h1>
            <div style={{display:'flex',gap:8,marginBottom:24}}>
              {[['pendencias','Pendências',totalPendencias],['atualizacoes','Atualizações',totalAtualizacoesNaoLidas]].map(([k,l,cnt]) => (
                <button key={k} onClick={() => { setAbaAtiva(k); if (k === 'atualizacoes') marcarAtualizacoesLidas(); }}
                  style={{padding:'8px 20px',borderRadius:999,fontWeight:700,fontSize:13,border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:6,
                    background:abaAtiva===k?'#5B3CE4':'#EDE8F5',color:abaAtiva===k?'#fff':'#6E6878'}}>
                  {l}
                  {cnt>0&&<span style={{background:abaAtiva===k?'rgba(255,255,255,0.25)':'#C5462A',color:'#fff',fontSize:11,fontWeight:800,padding:'1px 7px',borderRadius:999}}>{cnt}</span>}
                </button>
              ))}
            </div>
          </div>
          {carregando ? (
            <p style={{color:'#9A93A4',fontSize:13,textAlign:'center',paddingTop:40}}>Carregando...</p>
          ) : abaAtiva === 'pendencias' ? (
            totalPendencias === 0 ? (
              <div style={{textAlign:'center',padding:'60px 0'}}>
                <div style={{width:56,height:56,borderRadius:18,background:'#EDE8FC',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px',fontSize:22,fontWeight:800,color:'#7C5CBF'}}>ok</div>
                <p style={{fontSize:14,fontWeight:700,color:'#211C2B',margin:'0 0 4px'}}>Tudo resolvido</p>
                <p style={{fontSize:12.5,margin:0,color:'#9A93A4'}}>Sem pendências no momento.</p>
              </div>
            ) : (
              <>
                {solicitacoesClube.map(m => renderSolicitacaoClube(m))}
                {pendencias.map(n => renderNotif(n))}
              </>
            )
          ) : (
            atualizacoes.length === 0 && minhasSolicitacoes.length === 0 ? (
              <div style={{textAlign:'center',padding:'60px 0'}}>
                <div style={{width:56,height:56,borderRadius:18,background:'#EDE8FC',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px'}}>
                  <Bell size={24} color="#5B3CE4" />
                </div>
                <p style={{fontSize:14,fontWeight:700,color:'#211C2B',margin:'0 0 4px'}}>Sem atualizações</p>
                <p style={{fontSize:12.5,margin:0,color:'#9A93A4'}}>Nada por aqui ainda.</p>
              </div>
            ) : (
              <>
                {minhasSolicitacoes.map(s => (
                  <div key={s.id} className="border rounded-[14px] p-[14px] mb-3 bg-white border-[#EBE8F0]">
                    <div className="flex gap-3 mb-3">
                      <div className="w-[38px] h-[38px] rounded-[11px] flex items-center justify-center flex-shrink-0" style={{background:'#FBF1DC'}}>
                        <Users size={17} color="#9A6B12" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-ink-900 truncate">{s.clubes?.nome || 'Clube'}</p>
                        <p className="text-xs text-amber-600 mt-0.5">Aguardando aprovação do admin</p>
                      </div>
                    </div>
                    {onIrParaClubes && (
                      <button onClick={() => onIrParaClubes(s.clube_id)}
                        className="w-full py-2 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors">
                        Ver clube
                      </button>
                    )}
                  </div>
                ))}
                {atualizacoes.map(n => renderNotif(n))}
              </>
            )
          )}
          <div style={{height:40}} />
        </div>

        {/* Right: loan flow panel */}
        <div style={{width:320,flexShrink:0,background:'#F6F5FB',borderLeft:'1px solid #EDE9F5',overflowY:'auto',scrollbarWidth:'none',padding:24}}>
          <p style={{fontSize:10,fontWeight:800,letterSpacing:'.15em',textTransform:'uppercase',color:'#5B3CE4',margin:'0 0 20px'}}>FLUXO DE EMPRÉSTIMO</p>
          {!notifComFluxo ? (
            <p style={{color:'#9A93A4',fontSize:13}}>Nenhum empréstimo ativo no momento.</p>
          ) : (() => {
            const emp = notifComFluxo.emprestimos;
            const livro = emp.livros;
            const leitorNome = notifComFluxo.meta?.nome || notifComFluxo.meta?.leitor_nome || 'Leitor';
            const steps = [
              {label:'Pedido enviado',desc:`${leitorNome} solicitou o empréstimo`},
              {label:'Aguardando aprovação',desc:'Aceite ou recuse o pedido'},
              {label:'Combinar entrega',desc:'Definir local e data'},
              {label:'Em leitura',desc:`Livro com ${leitorNome}`},
              {label:'Devolução',desc:'Confirmar retorno do livro'},
            ];
            const cur = emp.status==='pendente'?1:emp.status==='aprovado'?2:emp.status==='em_andamento'?3:emp.status==='devolvido'?4:1;
            return (
              <>
                <div style={{display:'flex',gap:12,marginBottom:20,padding:'12px',background:'#fff',borderRadius:14,boxShadow:'0 1px 3px rgba(33,28,43,0.07)'}}>
                  <div style={{width:48,height:60,borderRadius:8,background:'#2D3B55',flexShrink:0,overflow:'hidden'}}>
                    {livro.capa_url&&<img src={livro.capa_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />}
                  </div>
                  <div>
                    <p style={{fontWeight:700,fontSize:14,color:'#211C2B',margin:'0 0 4px'}}>{livro.titulo}</p>
                    <p style={{fontSize:12,color:'#9A93A4',margin:0}}>Solicitado por <strong style={{color:'#211C2B'}}>{leitorNome}</strong></p>
                  </div>
                </div>
                {steps.map((step,i) => {
                  const done=i<cur, active=i===cur;
                  return (
                    <div key={i} style={{display:'flex',gap:12,opacity:done||active?1:0.45,marginBottom:4}}>
                      <div style={{display:'flex',flexDirection:'column',alignItems:'center',width:24,flexShrink:0}}>
                        <div style={{width:22,height:22,borderRadius:'50%',border:`2px solid ${done?'#1FA971':active?'#5B3CE4':'#BDB7C5'}`,background:done?'#1FA971':active?'#5B3CE4':'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                          {done&&<svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </div>
                        {i<steps.length-1&&<div style={{width:2,flex:1,minHeight:16,background:done?'#1FA971':'#EDE9F5',margin:'3px 0'}} />}
                      </div>
                      <div style={{paddingBottom:14}}>
                        <p style={{fontWeight:active?700:500,fontSize:13,color:active?'#211C2B':'#9A93A4',margin:'1px 0 2px'}}>{step.label}</p>
                        <p style={{fontSize:11,color:'#9A93A4',margin:0}}>{step.desc}</p>
                      </div>
                    </div>
                  );
                })}
                {notifComFluxo.tipo==='nova_solicitacao'&&(
                  <div style={{marginTop:16,display:'flex',flexDirection:'column',gap:8}}>
                    <button onClick={() => handleAprovar(notifComFluxo)}
                      style={{width:'100%',padding:'13px',borderRadius:14,background:'#5B3CE4',color:'#fff',fontWeight:700,fontSize:14,border:'none',cursor:'pointer'}}>
                      Aprovar pedido
                    </button>
                    <button onClick={() => handleRecusar(notifComFluxo)}
                      style={{width:'100%',padding:'12px',borderRadius:14,background:'transparent',color:'#211C2B',fontWeight:700,fontSize:14,border:'1.5px solid #EDE9F5',cursor:'pointer'}}>
                      Recusar
                    </button>
                    <p style={{fontSize:11,color:'#9A93A4',textAlign:'center',margin:0}}>Ao aprovar, vocês poderão combinar a entrega pelo chat.</p>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Modals — position:fixed, visíveis em todos os tamanhos de tela */}
      {cancelamentoAberto && (
        <div className="fixed inset-0 flex items-end lg:items-center justify-center z-50 px-0 lg:px-4" style={{background:'rgba(0,0,0,0.35)'}}>
          <div className="bg-white rounded-t-2xl lg:rounded-2xl p-6 w-full max-w-md lg:shadow-2xl">
            <p className="text-sm font-bold text-ink-900 mb-1">Cancelar emprestimo</p>
            <p className="text-xs text-ink-400 mb-4">Você pode deixar uma mensagem explicando o motivo (opcional).</p>
            <textarea className="w-full border border-line rounded-xl px-3 py-2.5 text-sm bg-paper outline-none focus:border-line resize-none mb-4"
              rows={3} placeholder="Motivo (opcional)..." value={cancelJustificativa} onChange={e => setCancelJustificativa(e.target.value)} />
            <div className="flex gap-2">
              <button onClick={() => setCancelamentoAberto(null)} className="flex-1 py-2.5 text-xs border border-line text-ink-500 rounded-xl hover:bg-paper transition-colors font-semibold">Voltar</button>
              <button onClick={handleCancelarEmprestimo} disabled={salvandoCancelamento} className="flex-1 py-2.5 text-xs bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50 font-bold">
                {salvandoCancelamento ? 'Cancelando...' : 'Cancelar emprestimo'}
              </button>
            </div>
          </div>
        </div>
      )}
      {avaliacaoAberta && (
        <div className="fixed inset-0 flex items-center justify-center z-50 px-4" style={{background:'rgba(0,0,0,0.3)'}}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm max-h-[90vh] overflow-y-auto">
            <p className="text-sm font-medium text-ink-900 mb-1">Avaliar empréstimo</p>
            <p className="text-xs text-ink-400 mb-4">{avaliacaoAberta.emprestimos?.livros?.titulo}</p>
            <div className="flex flex-col gap-3 mb-4">
              <SecaoEstrelas label="Avaliação do livro" estrelas={estrelasLivro} onEstrelas={setEstrelasLivro} comentario={comentarioLivro} onComentario={setComentarioLivro} placeholder="O que você achou do conteúdo?" />
              <SecaoEstrelas label="Avaliação do dono" estrelas={estrelasDono} onEstrelas={setEstrelasDono} comentario={comentarioDono} onComentario={setComentarioDono} placeholder="O dono foi atencioso e comunicou bem?" />
            </div>
            <div className="flex gap-2">
              <button onClick={handleSalvarAvaliacao} disabled={salvando} className="flex-1 py-2.5 text-xs bg-brand text-white rounded-xl disabled:opacity-50">{salvando?'Salvando...':'Enviar avaliação'}</button>
              <button onClick={() => setAvaliacaoAberta(null)} className="flex-1 py-2.5 text-xs border border-line text-ink-500 rounded-xl">Depois</button>
            </div>
          </div>
        </div>
      )}
      {avaliacaoLeitorAberta && (
        <div className="fixed inset-0 flex items-center justify-center z-50 px-4" style={{background:'rgba(0,0,0,0.3)'}}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm max-h-[90vh] overflow-y-auto">
            <p className="text-sm font-medium text-ink-900 mb-1">Avaliar leitor</p>
            <p className="text-xs text-ink-400 mb-4">{avaliacaoLeitorAberta.emprestimos?.livros?.titulo}</p>
            <div className="flex flex-col gap-3 mb-4">
              <div>
                <p className="text-xs font-semibold text-ink-700 mb-2">Como foi o leitor?</p>
                <div className="flex gap-1">{[1,2,3,4,5].map(s => (<button key={s} onClick={() => setEstrelasLeitorAv(estrelasLeitorAv===s?0:s)} className={`text-2xl ${s<=estrelasLeitorAv?'text-amber-400':'text-ink-300'}`}>★</button>))}</div>
              </div>
              <div>
                <p className="text-xs font-semibold text-ink-700 mb-1">Comentário (opcional)</p>
                <textarea className="w-full border border-line rounded-lg p-2.5 text-xs text-ink-900 bg-paper resize-none focus:outline-none" rows={3} placeholder="O leitor foi pontual e cuidadoso?" value={comentarioLeitorAv} onChange={e => setComentarioLeitorAv(e.target.value)} />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={handleSalvarAvaliacaoLeitor} disabled={salvando} className="flex-1 py-2.5 text-xs bg-brand text-white rounded-xl disabled:opacity-50">{salvando?'Salvando...':'Enviar avaliação'}</button>
              <button onClick={() => setAvaliacaoLeitorAberta(null)} className="flex-1 py-2.5 text-xs border border-line text-ink-500 rounded-xl">Depois</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Notificacoes;
